import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSafeTwilioConversations } from '../contexts/TwilioConversationsContext';
import { FeatureFlag, useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { getUnreadCount as getNotificationUnreadCount } from '../services/notificationService';
import './MobileFooter.css';
import { useSoftKeyboardOpen } from '../hooks/useSoftKeyboardOpen';

import {
  Add,
  CalendarMonth,
  MenuBook,
  NotificationsNone,
} from '@mui/icons-material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
interface MobileFooterProps {
  onAddClick?: () => void;
}

const MobileFooter: React.FC<MobileFooterProps> = ({ onAddClick }) => {
  const { t } = useTranslation('common');
  const { conversations } = useSafeTwilioConversations();
  const { isFeatureEnabled } = useFeatureFlags();
  const chatFeatureEnabled = isFeatureEnabled(FeatureFlag.CHAT_FEATURE);
  const [notifUnreadCount, setNotifUnreadCount] = useState(0);

  // Message unread count logic
  const countUnreadMessages = useCallback(() => {
    if (!conversations || !Array.isArray(conversations)) return 0;
    let count = 0;
    for (const conv of conversations) {
      if ((conv.unreadMessagesCount && conv.unreadMessagesCount > 0) || conv.hasUnreadMessages) {
        count++;
      }
    }
    return count;
  }, [conversations]);

  const messageUnreadCount = countUnreadMessages();
  const softKeyboardOpen = useSoftKeyboardOpen();


  // Notification unread count logic
  const fetchNotifCount = async () => {
    try {
      const count = await getNotificationUnreadCount();
      setNotifUnreadCount(count);
    } catch (error) {
      // Silent fail
    }
  };

  useEffect(() => {
    fetchNotifCount();
    const interval = setInterval(fetchNotifCount, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <nav
      className={`mobile-footer${softKeyboardOpen ? ' mobile-footer--soft-keyboard-open' : ''}`}
      aria-hidden={softKeyboardOpen ? true : undefined}
    >
      <div className="mobile-footer-inner">
        <NavLink
          to="/app/planning"
          className={({ isActive }) =>
            isActive ? 'mobile-footer-item active' : 'mobile-footer-item'
          }
        >
          <CalendarMonth className="mobile-footer-icon" />
        </NavLink>

        <NavLink
          to="/app/reservations"
          className={({ isActive }) =>
            isActive ? 'mobile-footer-item active' : 'mobile-footer-item'
          }
        >
          <MenuBook className="mobile-footer-icon" />
        </NavLink>

        {/* Bouton central : un seul élément (cercle taupe + icône blanche calendrier+) */}
        <button
          type="button"
          className="mobile-footer-add"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            onAddClick?.();
          }}
          aria-label={t('mobileFooter.addAriaLabel')}
        >
          <span className="mobile-footer-add-icon calendar-plus">
            <CalendarMonth />
            <Add className="icon-overlay" />
          </span>
        </button>

        {chatFeatureEnabled ? (
          <NavLink
            to="/app/messages"
            className={({ isActive }) =>
              isActive ? 'mobile-footer-item active' : 'mobile-footer-item'
            }
          >
            <div className="mobile-footer-icon-wrapper">
              <SmsOutlinedIcon className="mobile-footer-icon" />
              {messageUnreadCount > 0 && (
                <span className="mobile-footer-badge">
                  {messageUnreadCount > 9 ? '9+' : messageUnreadCount}
                </span>
              )}
            </div>
          </NavLink>
        ) : (
          <NavLink
            to="/app/notifications"
            className={({ isActive }) =>
              isActive ? 'mobile-footer-item active' : 'mobile-footer-item'
            }
            aria-label={t('mobileFooter.notificationsAriaLabel', { defaultValue: 'Notifications' })}
          >
            <div className="mobile-footer-icon-wrapper">
              <NotificationsNone className="mobile-footer-icon" />
              {notifUnreadCount > 0 && (
                <span className="mobile-footer-badge">
                  {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
                </span>
              )}
            </div>
          </NavLink>
        )}

        {chatFeatureEnabled && (
        <NavLink
          to="/app/notifications"
          className={({ isActive }) =>
            isActive ? 'mobile-footer-item active' : 'mobile-footer-item'
          }
        >
          <div className="mobile-footer-icon-wrapper">
            <NotificationsNone className="mobile-footer-icon" />
            {notifUnreadCount > 0 && (
              <span className="mobile-footer-badge">
                {notifUnreadCount > 9 ? '9+' : notifUnreadCount}
              </span>
            )}
          </div>
        </NavLink>
        )}
      </div>

      {/* Indicateur de safe area pour iPhone X et plus récents */}
      <div className="mobile-footer-safe-area" />
    </nav>
  );
};

export default MobileFooter;
